import Reveal from "@/components/site/Reveal";
import { CLIENTS, WHATSAPP_LINK } from "@/data/content";
import { FaBuildingColumns, FaArrowRight, FaPlus } from "react-icons/fa6";

const ClientCard = ({ client, idx }) => (
  <div
    tabIndex={0}
    key={`${client.name}-${idx}`}
    className="group relative flex h-[180px] w-[300px] sm:w-[340px] md:w-[360px] lg:w-[380px] shrink-0 flex-col items-center justify-center rounded-[22px] border border-[#E8D8B0] bg-white p-6 text-center shadow-soft transition-all duration-300 hover:-translate-y-2 hover:border-gold hover:shadow-[0_16px_36px_rgba(198,161,91,0.22)] focus:outline-none focus:ring-2 focus:ring-gold/50 cursor-pointer select-none overflow-hidden"
  >
    {client.isPlaceholder ? (
      <div className="flex flex-col items-center justify-center space-y-2 text-center p-2">
        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-dashed border-gold/60 bg-[#FCF9F2] text-gold transition-transform duration-300 group-hover:scale-110 group-hover:border-gold group-hover:bg-gold group-hover:text-white">
          <FaPlus className="text-sm" />
        </div>
        <span className="font-display text-lg font-bold tracking-tight text-navy/80 transition-colors duration-300 group-hover:text-gold">
          {client.name || "Coming Soon"}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-paragraph/50">
          {client.category || "Future Partner"}
        </span>
      </div>
    ) : client.logo ? (
      <div className="flex items-center justify-center w-full h-full p-2">
        <img
          src={client.logo}
          alt={`${client.name} — Hiring Partner of PLACE8 Talent Recruitment Consultancy`}
          loading="lazy"
          className={`max-h-[105px] max-w-[85%] w-auto h-auto object-contain transition-transform duration-300 group-hover:scale-[1.03] ${client.logoClass || ""}`}
        />
      </div>
    ) : (
      <>
        <span className="font-display text-xl font-bold tracking-tight text-navy transition-colors duration-300 group-hover:text-gold sm:text-2xl">
          {client.name}
        </span>
        <div className="my-2.5 h-0.5 w-8 bg-gold/30 transition-all duration-300 group-hover:w-14 group-hover:bg-gold" />
        <p className="text-xs font-semibold uppercase tracking-wider text-paragraph/75 transition-colors duration-300 group-hover:text-navy">
          {client.category}
        </p>
        {client.highlight && (
          <span className="mt-1 text-[11px] font-medium text-paragraph/60">
            {client.highlight}
          </span>
        )}
      </>
    )}
  </div>
);

const Clients = () => {
  // Quadruple CLIENTS array for continuous seamless infinite looping
  const carouselCards = [...CLIENTS, ...CLIENTS, ...CLIENTS, ...CLIENTS];

  return (
    <section
      id="clients"
      data-testid="clients-section"
      className="relative overflow-hidden bg-gradient-to-b from-white via-[#FDF8F0] to-white py-20 md:py-28"
    >
      <div className="mx-auto max-w-[1300px] px-6 md:px-10">
        <Reveal className="mb-14 text-center max-w-3xl mx-auto">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-navy shadow-soft">
            <FaBuildingColumns className="text-gold" /> Trusted Hiring Partners
          </p>
          <h2 className="font-display text-3xl font-extralight leading-[1.1] tracking-tightest text-charcoal sm:text-4xl md:text-5xl">
            Powering Recruitment For India's <span className="italic text-gradient-gold">Leading Enterprises.</span>
          </h2>
          <p className="mt-3 text-sm text-paragraph md:text-base">
            From technology giants and financial institutions to high-growth consumer platforms.
          </p>
        </Reveal>
      </div>

      {/* Marquee Carousel Container */}
      <div className="marquee-container relative w-full overflow-hidden py-4">
        {/* Soft edge blur masks */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 lg:w-48 bg-gradient-to-r from-white via-white/80 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 lg:w-48 bg-gradient-to-l from-white via-white/80 to-transparent z-10" />

        {/* Single Row: Continuous Auto-scrolling Marquee */}
        <div className="flex w-full overflow-hidden">
          <div className="animate-marquee-left flex gap-8 pr-8">
            {carouselCards.map((client, idx) => (
              <ClientCard key={`carousel-${client.name}-${idx}`} client={client} idx={idx} />
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1300px] px-6 md:px-10">
        {/* Banner CTA */}
        <Reveal delay={0.2} className="mt-14 rounded-3xl bg-navy p-8 text-white shadow-luxe flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl font-light">Looking to Partner with PLACE8 for Your Hiring Needs?</h3>
            <p className="mt-1 text-xs text-white/70">Join 55+ enterprise and startup hiring partners across India.</p>
          </div>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold shrink-0 inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-wider text-navy transition-transform hover:-translate-y-0.5"
          >
            Become a Hiring Partner <FaArrowRight className="text-xs" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default Clients;
